import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { axiosInstance } from "../App";
import { FolderKanban, CheckCircle2, Clock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

function DashboardStats() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!stats) return null;

  // Üst sıra - 4 ana istatistik (Top row - 4 main statistics)
  const mainStatCards = [
    {
      title: t('companyTotalProjects'),
      description: t('companyProjectsDesc'),
      value: stats.company_total_projects,
      icon: FolderKanban,
      color: "text-blue-500",
      bg: "bg-blue-50"
    },
    {
      title: t('userCreatedProjects'),
      description: t('projectsYouCreated'),
      value: stats.user_created_projects,
      icon: FolderKanban,
      color: "text-green-500",
      bg: "bg-green-50"
    },
    {
      title: t('userCreatedTasks'),
      description: t('tasksYouCreated'),
      value: stats.user_created_tasks,
      icon: CheckCircle2,
      color: "text-purple-500",
      bg: "bg-purple-50"
    },
    {
      title: t('userAssignedTasks'),
      description: t('tasksAssignedToYou'),
      value: stats.user_assigned_tasks,
      icon: User,
      color: "text-orange-500",
      bg: "bg-orange-50"
    }
  ];

  // Alt sıra - Atanan görevlerin durum dağılımı (Bottom row - Assigned tasks status distribution)
  const assignedStatusCards = [
    {
      title: t('notStarted'),
      description: t('todoTasks'),
      value: stats.assigned_tasks_by_status?.todo || 0,
      icon: Clock,
      color: "text-gray-500",
      bg: "bg-gray-50"
    },
    {
      title: t('inProgress'),
      description: t('ongoingTasks'),
      value: stats.assigned_tasks_by_status?.in_progress || 0,
      icon: Clock,
      color: "text-blue-500",
      bg: "bg-blue-50"
    },
    {
      title: t('completed'),
      description: t('finishedTasks'),
      value: stats.assigned_tasks_by_status?.done || 0,
      icon: CheckCircle2,
      color: "text-green-500",
      bg: "bg-green-50"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Üst Sıra - Ana İstatistikler (Top Row - Main Statistics) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {mainStatCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className={`${stat.bg} p-3 rounded-lg`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold mb-2">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Alt Sıra - Atanan Görevlerin Durum Dağılımı (Bottom Row - Assigned Tasks Status) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {assignedStatusCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className={`${stat.bg} p-3 rounded-lg`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold mb-2">{stat.value}</p>
                  <p className="text-xs text-gray-400">{stat.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Project Progress */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle>{t('projectProgress')}</CardTitle>
          <CardDescription>{t('recentProjects')}</CardDescription>
        </CardHeader>
        <CardContent>
          {stats.project_stats.length === 0 ? (
            <p className="text-center text-gray-500 py-8">{t('noProjects')}</p>
          ) : (
            <div className="space-y-6">
              {stats.project_stats.map((project) => (
                <div key={project.id}>
                  <div className="flex justify-between mb-2">
                    <div>
                      <span className="font-medium">{project.name}</span>
                      <p className="text-sm text-gray-500">
                        {project.done_tasks} / {project.total_tasks} görev tamamlandı
                      </p>
                    </div>
                    <span className="text-lg font-bold text-blue-600">{project.completion_percentage}%</span>
                  </div>
                  <Progress value={project.completion_percentage} className="h-3" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default DashboardStats;
